import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { TIME_RANGE } from '../../constants';
import { getTimeRangeValue } from '../../utils';

import {
  getDoctorManagementList,
  getDoctorManagementStatCardsData,
  getDoctorProfileCompletionData,
  getDoctorRegistrationTrends
} from '../../services/doctor-management';

import { useS3Uploader } from './../useS3Uploader';
import { useTableData } from './../useTableData';
import { getRealmUserInfo } from '../../services';

import { DoctorManagementRow } from '../../pages/DoctorManagement/types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/* -------------------------------- TYPES -------------------------------- */

type RawAppointmentRelation = {
  patientUser?: {
    patientUserDocuments?: {
      imageContent?: string;
      patientId?: string;
    };
  };
};

/* ---------------------------- HOOK START ---------------------------- */

export const useDoctorManagement = (
  selectedValueDoughnutChart: string,
  selectedValueVerificationTrends: string,
  rowsPerPage: number = 10,
  page: number = 0
) => {
  const { t } = useTranslation();

  const userRole = localStorage.getItem('userRole') || '';
  const accessToken = localStorage.getItem('user_access_token') || '';

  const { generateSignedUrl } = useS3Uploader();

  /* ---------------------------- TABLE STATE ---------------------------- */

  const {
    tableRows,
    setTableRows,
    hasMore,
    setHasMore,
    totalCount,
    setTotalCount,
    pageTimestamps,
    setPageTimestamps
  } = useTableData<DoctorManagementRow>();

  /* ---------------------------- HELPERS ---------------------------- */

  const mapDoughnutRangeForBackend = (value: string): string => {
    switch (value) {
      case TIME_RANGE.WEEKLY:
        return 'weekly';
      case TIME_RANGE.MONTHLY:
        return 'monthly';
      case TIME_RANGE.YEARLY:
        return 'yearly';
      default:
        return value;
    }
  };

  const processTableData = async (
    rawData: DoctorManagementRow[],
    currentPage: number
  ): Promise<DoctorManagementRow[]> => {
    return Promise.all(
      rawData.map(
        async (item: DoctorManagementRow & { connectedUsers: RawAppointmentRelation[] }, index) => {
          const doctorImageUrl = item.image ? await generateSignedUrl(item.image) : '';

          const connectedUsers = await Promise.all(
            (item.connectedUsers || [])
              .filter(
                (doc: RawAppointmentRelation) =>
                  !!doc?.patientUser?.patientUserDocuments?.imageContent &&
                  !!doc?.patientUser?.patientUserDocuments?.patientId
              )
              .map(async (doc) => {
                const docData = doc.patientUser!.patientUserDocuments!;
                const signedUrl = await generateSignedUrl(docData.imageContent!);

                const { firstName, lastName } = await getRealmUserInfo(
                  docData.patientId!,
                  accessToken
                );

                return {
                  name: `${firstName} ${lastName}`,
                  image: signedUrl
                };
              })
          );

          const [date, time] = item.verifiedOn?.includes(' ')
            ? item.verifiedOn.split(' ')
            : ['', ''];

          return {
            ...item,
            sNo: currentPage * rowsPerPage + index + 1,
            image: doctorImageUrl,
            date,
            time,
            role: item.role || t('roleNotAssigned'),
            connectedUsers
          };
        }
      )
    );
  };

  /* ---------------------------- QUERIES ---------------------------- */

  const profileCompletionQuery = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ['doctorProfileCompletion', selectedValueDoughnutChart],
    queryFn: () =>
      getDoctorProfileCompletionData({
        rangeType: mapDoughnutRangeForBackend(selectedValueDoughnutChart)
      })
  });

  const registrationTrendsQuery = useQuery({
    queryKey: ['doctorRegistrationTrends', selectedValueVerificationTrends],
    queryFn: () =>
      getDoctorRegistrationTrends({
        rangeType: getTimeRangeValue(selectedValueVerificationTrends)
      })
  });

  const statCardsQuery = useQuery({
    queryKey: ['doctorManagementStats', userRole],
    queryFn: () => getDoctorManagementStatCardsData()
  });

  const tableQuery = useQuery({
    queryKey: ['doctorTable', page, rowsPerPage],
    queryFn: async () => {
      const prevTimestamp = page === 0 ? undefined : pageTimestamps.get(page - 1);

      const result = await getDoctorManagementList(rowsPerPage, prevTimestamp);

      const rows = await processTableData(result.data || [], page);

      return {
        ...result,
        rows
      };
    }
  });
  useEffect(() => {
    if (!tableQuery.data) return;

    const data = tableQuery.data;

    setTableRows(data.rows);
    setHasMore(data.hasMore || false);
    setTotalCount(data.totalCount || 0);

    if (data.lastUpdatedAt) {
      setPageTimestamps((prev) => {
        const updated = new Map(prev);
        updated.set(page, data.lastUpdatedAt);
        return updated;
      });
    }
  }, [tableQuery.data, page]);

  /* ---------------------------- DERIVED DATA ---------------------------- */

  const statCards = statCardsQuery.data || {};

  const doctorStats = {
    totalDoctors: statCards.totalDoctorsCount?.aggregate?.count || 0,
    verifiedDoctors: statCards.verifiedDoctorsCount?.aggregate?.count || 0,
    completedProfiles: statCards.completedProfileDoctorsCount?.aggregate?.count || 0,
    incompletedProfiles:
      (statCards.totalDoctorsCount?.aggregate?.count || 0) -
      (statCards.completedProfileDoctorsCount?.aggregate?.count || 0),
    activeDoctors: statCards.get_active_doctors_count?.[0]?.count || 0
  };

  /* ---------------------------- RETURN ---------------------------- */

  return {
    doctorStats,
    profileCompletionData: profileCompletionQuery.data,
    registrationTrendsData: registrationTrendsQuery.data,
    tableRows,
    totalCount,
    hasMore,

    loading: statCardsQuery.isLoading,

    tableLoading: tableQuery.isFetching,

    error:
      profileCompletionQuery.error || registrationTrendsQuery.error || tableQuery.error || false
  };
};
